<template>
  <div class="popin-shell">
    <header class="shell-theme-header">
      <div class="shell-header-bar">
        <div class="container shell-header-row">
          <router-link to="/" class="shell-brand" @touchstart.native.passive="closeMobileNav">
            <img src="/theme/img/popin-logo.jpg" alt="Popin" />
          </router-link>

          <div v-if="notificationsEnabled" class="shell-mobile-bell d-lg-none">
            <NotificationBell />
          </div>

          <button
            class="shell-toggler d-lg-none"
            type="button"
            aria-label="Toggle navigation"
            :aria-expanded="isMobileNavOpen ? 'true' : 'false'"
            @click="toggleMobileNav"
          >
            <span class="shell-toggler-bar"></span>
            <span class="shell-toggler-bar"></span>
            <span class="shell-toggler-bar"></span>
          </button>

          <div class="shell-desktop-area d-none d-lg-flex">
            <div class="shell-side-top">
              <div class="shell-page-title">
                <h1>{{ currentSection }}</h1>
              </div>
              <div class="shell-branch-name">
                <div class="shell-br-name">
                  {{ coname || 'Popin' }}
                  <span>{{ branchname }}</span>
                </div>
              </div>
            </div>

            <ul class="shell-nav-list">
              <li v-for="item in navItems" :key="item.path" class="shell-nav-item">
                <router-link
                  class="shell-nav-link"
                  :class="{ active: isActive(item) }"
                  :to="item.path"
                >
                  <img :src="item.icon" :alt="item.label" />
                  <span>{{ item.label }}</span>
                </router-link>
              </li>
            </ul>

            <div class="shell-actions">
              <div
                v-if="showLocationSelector"
                class="shell-location-wrap"
                @keydown.esc="closeLocationMenu"
              >
                <button
                  type="button"
                  class="shell-location"
                  :aria-expanded="isLocationMenuOpen ? 'true' : 'false'"
                  @click="toggleLocationMenu"
                >
                  <span>{{ currentLocationLabel }}</span>
                  <span class="shell-location__caret">&#9662;</span>
                </button>

                <div v-if="isLocationMenuOpen" class="shell-location-menu">
                  <a
                    v-for="location in locations"
                    :key="location.code || location.label"
                    class="shell-location-menu__item"
                    :class="{ active: location.active, disabled: isChangingBranch }"
                    href="#"
                    @click.prevent="handleLocationSelect(location)"
                  >
                    {{ location.label }}
                  </a>
                </div>
              </div>

              <NotificationBell v-if="notificationsEnabled" />

              <button class="shell-logout" type="button" @click="handleLogout">
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <transition name="shell-drawer">
        <div v-if="isMobileNavOpen" class="shell-drawer d-lg-none">
          <div class="shell-drawer-header">
            <div class="shell-drawer-title">{{ coname || 'Popin' }}</div>
            <div v-if="branchname" class="shell-drawer-sub">{{ branchname }}</div>
          </div>

          <!-- Branch list -->
          <div class="shell-drawer-branch" v-if="locations.length">
            <button class="shell-drawer-branch-trigger" @click="isBranchListOpen = !isBranchListOpen">
              <span><i class="las la-map-marker-alt"></i> {{ currentLocationLabel }}</span>
              <i class="las" :class="isBranchListOpen ? 'la-angle-up' : 'la-angle-down'"></i>
            </button>
            <div v-if="isBranchListOpen" class="shell-drawer-branch-list">
              <a
                v-for="loc in locations"
                :key="loc.code"
                class="shell-drawer-branch-item"
                :class="{ active: loc.active, disabled: isChangingBranch }"
                href="#"
                @click.prevent="handleLocationSelect(loc); isBranchListOpen = false"
              >
                <span class="shell-drawer-branch-dot"></span>
                {{ loc.label }}
              </a>
            </div>
          </div>

          <!-- Information & Policies -->
          <nav class="shell-drawer-nav">
            <template v-if="notificationsEnabled">
              <router-link to="/notifications" class="shell-drawer-link" @click.native="closeMobileNav">
                <i class="las la-bell"></i> Notifications
                <span v-if="unreadNotifications" class="shell-drawer-count">{{ unreadNotifications }}</span>
              </router-link>
            </template>
            <div class="shell-drawer-section-label" :class="{ 'drawer-section-gap': notificationsEnabled }">Information</div>
            <router-link
              v-for="link in infoLinks"
              :key="link.path"
              :to="link.path"
              class="shell-drawer-link"
              @click.native="closeMobileNav"
            >
              <i class="las" :class="link.icon"></i> {{ link.label }}
            </router-link>

            <div class="shell-drawer-section-label drawer-section-gap">Policies</div>
            <router-link
              v-for="link in policyLinks"
              :key="link.path"
              :to="link.path"
              class="shell-drawer-link"
              @click.native="closeMobileNav"
            >
              <i class="las" :class="link.icon"></i> {{ link.label }}
            </router-link>
          </nav>

          <!-- Logout pinned at bottom -->
          <button class="shell-drawer-logout" type="button" @click="handleLogout">
            <i class="las la-sign-out-alt"></i> Logout
          </button>
        </div>
      </transition>

      <div
        v-if="isMobileNavOpen"
        class="shell-drawer-backdrop d-lg-none"
        @click="closeMobileNav"
      ></div>
    </header>

    <div class="shell-mobile-bottom d-lg-none">
      <div class="shell-mobile-bottom-row">
        <router-link
          v-for="item in navItems"
          :key="`${item.path}-bottom`"
          :to="item.path"
          class="shell-mobile-bottom-link"
          :class="{ active: isActive(item) }"
        >
          <img :src="item.icon" :alt="item.label" />
          <span>{{ item.label }}</span>
        </router-link>
      </div>
    </div>

    <main class="shell-main" :style="shellBgStyle">
      <router-view :key="$route.path" />
    </main>

    <notifications group="default" />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters, mapActions } from 'vuex'
import { makeBgStyle } from '@/config/dashboardBackgrounds.js'
import NotificationBell from '@/components/NotificationBell.vue'

const NAV_BY_ROLE = {
  vendor: [
    { path: '/', label: 'Home', icon: '/theme/img/home.svg' },
    { path: '/vendor/product', label: 'Products', icon: '/theme/img/package.svg' },
    { path: '/vendor/order/list', label: 'Orders', icon: '/theme/img/scroll.svg' },
    { path: '/vendor/event', label: 'Events', icon: '/theme/img/calendar-check.svg' },
    { path: '/profile', label: 'Profile', icon: '/theme/img/Reminder.svg' },
  ],
  technology: [
    { path: '/', label: 'Home', icon: '/theme/img/home.svg' },
    { path: '/ecommerce/technology-partners', label: 'Partners', icon: '/theme/img/Group 420.svg' },
    { path: '/ecommerce/activity-summary', label: 'Activity', icon: '/theme/img/Rental.svg' },
    { path: '/ecommerce/orders', label: 'Orders', icon: '/theme/img/scroll.svg' },
    { path: '/profile', label: 'Profile', icon: '/theme/img/Reminder.svg' },
  ],
  user: [
    { path: '/rental/dashboard', label: 'Rental', icon: '/theme/img/storefront.svg' },
    { path: '/sale/dashboard', label: 'Sale', icon: '/theme/img/scroll.svg' },
    { path: '/sale/mto/dashboard', label: 'MTO', icon: '/theme/img/scroll.svg' },
    { path: '/online/dashboard', label: 'Online', icon: '/theme/img/storefront.svg' },
    { path: '/event/dashboard', label: 'Events', icon: '/theme/img/calendar-check.svg' },
    { path: '/rental/master', label: 'Master', icon: '/theme/img/tailor.svg' },
  ],
}

export default {
  name: 'AppShell',
  components: { NotificationBell },
  data() {
    return {
      isMobileNavOpen: false,
      isLocationMenuOpen: false,
      isChangingBranch: false,
      isBranchListOpen: false,
      // Bumped when BG Master saves a new local background override, so
      // shellBgStyle re-evaluates without a route change.
      bgVersion: 0,
      locations: [],
      infoLinks: [
        { path: '/about-us', label: 'About Us', icon: 'la-info-circle' },
        { path: '/contact-us', label: 'Contact Us', icon: 'la-phone' },
        { path: '/store', label: 'Store', icon: 'la-store' },
        { path: '/blog', label: 'Blog', icon: 'la-newspaper' },
        { path: '/faq', label: "FAQ's", icon: 'la-question-circle' },
        { path: '/franchise-enquiry', label: 'Franchise Enquiry', icon: 'la-handshake' },
        { path: '/licences', label: 'Licences', icon: 'la-key' },
      ],
      policyLinks: [
        { path: '/cancellation-policy', label: 'Cancellation Policy', icon: 'la-ban' },
        { path: '/privacy-policy', label: 'Privacy Policy', icon: 'la-user-shield' },
        { path: '/terms-conditions', label: 'Terms & Conditions', icon: 'la-file-contract' },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', ['coname', 'branchname', 'branches', 'loc']),
    role() {
      return this.$store.state.auth.role || 'user'
    },
    navItems() {
      return NAV_BY_ROLE[this.role] || NAV_BY_ROLE.user
    },
    // In-app notifications are for admin-panel users (bearer-token sign-in);
    // customer / vendor / technology sessions have no inbox.
    notificationsEnabled() {
      return !!this.$store.state.auth.token && !['user', 'vendor', 'technology'].includes(this.role)
    },
    unreadNotifications() {
      return this.$store.state.notifications.unread
    },
    currentSection() {
      const active = this.navItems.find((item) => this.isActive(item))
      return active ? active.label : 'Dashboard'
    },
    currentLocationLabel() {
      const active = this.locations.find((l) => l.active)
      return active?.label || this.branchname || 'Select Location'
    },
    activeLocationCode() {
      const active = this.locations.find((l) => l.active)
      return active?.code || ''
    },
    showLocationSelector() {
      return this.locations.length > 0 || !!this.branchname
    },
    // The app background lives on .shell-main. Pick the image per module from
    // the current route and build it via makeBgStyle, which prefers any local
    // override saved by BG Master (localStorage `bgoverride:<path>`). Reading
    // bgVersion makes this recompute the moment a new background is uploaded.
    shellBgStyle() {
      this.bgVersion // reactive dependency — bumped on 'bg-override-changed'
      const p = this.$route.path
      const key = p.startsWith('/sale/mto') ? 'mto'
        : p.startsWith('/sale') ? 'sale'
        : p.startsWith('/online') ? 'online'
        : p.startsWith('/event') ? 'event'
        : 'rental'
      return makeBgStyle(key)
    },
  },
  watch: {
    // Watch `path` (not `fullPath`): only a real page change should close the
    // menus and reset scroll. Hash/query changes — e.g. the many <a href="#">
    // buttons on the Rental Bill — must NOT scroll the page to the top while
    // the user is editing.
    '$route.path'() {
      this.closeMobileNav()
      this.closeLocationMenu()
      // The shell scrolls inside .shell-main (not the window), so reset its
      // scroll position on navigation to mimic a fresh page load.
      this.$nextTick(() => {
        const main = this.$el && this.$el.querySelector('.shell-main')
        if (main) main.scrollTop = 0
      })
    },
    notificationsEnabled: {
      immediate: true,
      handler(on) {
        this.$store.dispatch(on ? 'notifications/startPolling' : 'notifications/stopPolling')
      },
    },
    branches: {
      immediate: true,
      handler(list) {
        if (list && list.length) {
          this.locations = this.normalizeBranches(list)
        }
      },
    },
    // Lock body scroll while the side drawer is open (prevents background
    // scroll on iOS and keeps the backdrop from being scrollable).
    // On iOS, position:fixed resets the scroll position, so we save/restore it
    // via body.style.top and window.scrollTo to prevent a page jump.
    isMobileNavOpen(open) {
      if (open) {
        const scrollY = window.scrollY || window.pageYOffset
        document.body.dataset.scrollY = scrollY
        document.body.style.top = `-${scrollY}px`
        document.body.classList.add('shell-drawer-open')
      } else {
        const scrollY = parseInt(document.body.dataset.scrollY || '0', 10)
        document.body.classList.remove('shell-drawer-open')
        document.body.style.top = ''
        if (scrollY) window.scrollTo(0, scrollY)
      }
    },
  },
  async mounted() {
    // BG Master fires this after saving a local background override.
    this._onBgOverrideChanged = () => { this.bgVersion++ }
    window.addEventListener('bg-override-changed', this._onBgOverrideChanged)
    await this.loadLocations()
  },
  beforeDestroy() {
    window.removeEventListener('bg-override-changed', this._onBgOverrideChanged)
    this.$store.dispatch('notifications/stopPolling')
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10)
    document.body.classList.remove('shell-drawer-open')
    document.body.style.top = ''
    if (scrollY) window.scrollTo(0, scrollY)
  },
  methods: {
    ...mapActions('auth', ['logout']),
    isActive(item) {
      if (item.path === '/') return this.$route.path === '/'
      return this.$route.path === item.path || this.$route.path.startsWith(`${item.path}/`)
    },
    toggleMobileNav() {
      this.isMobileNavOpen = !this.isMobileNavOpen
    },
    closeMobileNav() {
      this.isMobileNavOpen = false
    },
    toggleLocationMenu() {
      this.isLocationMenuOpen = !this.isLocationMenuOpen
    },
    onBranchDropdownChange(event) {
      const code = event.target.value
      const location = this.locations.find((l) => l.code === code)
      if (location) this.handleLocationSelect(location)
    },
    closeLocationMenu() {
      this.isLocationMenuOpen = false
    },
    async handleLocationSelect(location) {
      if (!location?.code || location.active || this.isChangingBranch) {
        this.closeLocationMenu()
        return
      }
      this.isChangingBranch = true
      try {
        const { data } = await axios.post('/api/admin/change-branch', {
          branch_code: location.code,
        })
        if (data?.success && data.session) {
          this.$store.commit('auth/MERGE_SESSION', data.session)
          if (Array.isArray(data.session.branches) && data.session.branches.length) {
            this.locations = this.normalizeBranches(data.session.branches)
          } else {
            this.locations = this.locations.map((l) => ({ ...l, active: l.code === location.code }))
          }
          window.location.reload()
        }
      } catch (e) {
        const msg = e?.response?.data?.message || 'Unable to switch branch.'
        if (this.$notify) this.$notify({ type: 'error', text: msg })
      } finally {
        this.isChangingBranch = false
        this.closeLocationMenu()
      }
    },
    normalizeBranches(list) {
      const current = this.loc
      return (list || [])
        .map((b) => {
          const code = b.code || b.Code || ''
          const label = b.description || b.Description || code
          if (!label) return null
          return {
            code,
            label,
            active: !!current && code === current,
          }
        })
        .filter(Boolean)
    },
    async loadLocations() {
      if (this.locations.length) return
      try {
        const { data } = await axios.get('/api/admin/rental/branches')
        const list = data?.branches || []
        if (list.length) this.locations = this.normalizeBranches(list)
        if (data?.currentLabel && !this.branchname) {
          this.$store.commit('auth/MERGE_SESSION', {
            branchname: data.currentLabel,
            loc: data.current,
          })
        }
      } catch {
        // Keep the static branch label if the backend cannot be reached.
      }
    },
    async handleLogout() {
      await this.logout()
      this.closeMobileNav()
      this.closeLocationMenu()
      if (this.$route.path !== '/login') this.$router.push('/login')
    },
  },
}
</script>

<style>
/* Not scoped: legacy main.css owns .navbar/.nav-btn/.navigation-bar/.header etc.
   To win specificity reliably across legacy pages we use unique shell-* names
   and keep them in a global stylesheet block. */

/* Legacy global theme bakes a 150px top padding into .main-section.inside
   to clear the old in-page navbar. The SPA shell already provides its own
   sticky header, so the extra space leaves a large gap above legacy
   components. Reset it just inside the shell.

   Likewise the legacy `margin-bottom: 90–110px` cleared the OLD fixed bottom
   nav. The shell's bottom nav is now a static flex item, so that margin is dead
   space that stretches the page root — making the background photo paint past
   the content (a stray band) with the shell colour showing below it. Zero it
   so the page root hugs its content. */
.shell-main .main-section.inside,
.shell-main .main-section {
  padding-top: 24px;
  margin-bottom: 0;
  /* Real bottom padding (not margin) so the last action-button row clears the
     bottom nav and stays scroll-reachable on iOS — WebKit ignores a child's
     bottom margin in an overflow-scroll container. Includes the safe-area inset. */
  padding-bottom: calc(32px + env(safe-area-inset-bottom, 0px));
}

/* AppShell already renders its own mobile bottom nav (.shell-mobile-bottom).
   Legacy per-page .navigation-bar / .navbar-item bars become duplicates on
   mobile, so suppress them inside the shell. */
@media (max-width: 991.98px) {
  .shell-main .navigation-bar,
  .shell-main .navbar-item {
    display: none !important;
  }
}

.popin-shell {
  /* Fixed-height flex shell: the shell itself never scrolls — only
     .shell-main does. This keeps the header and the bottom nav as static
     flex items so the footer stays pinned (on iOS, a position:fixed footer
     against the document viewport drifts during momentum/rubber-band scroll).
     100dvh tracks the iOS dynamic toolbar; 100vh is the fallback. */
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f7f8fc;
}

.shell-main {
  flex: 1;
  order: 1;            /* keep main between the header and the footer */
  min-height: 0;       /* allow the flex child to shrink and scroll */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  isolation: isolate;
  /* The background photo lives on .shell-main itself — a flex item that always
     spans the full area between the header and the bottom nav. background-
     attachment:scroll keeps it pinned to the visible frame (it does not scroll
     with the content), so it fills the whole area seamlessly down to the nav
     with no empty band, whether the page is short or scrolls. Solid colour is a
     fallback if the image fails to load. */
  background-color: #f7f8fc;
  background-image:
    linear-gradient(rgba(122, 126, 89, 0.54), rgba(122, 126, 89, 0.54)),
    url('/theme/img/bg2.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: scroll;
}

.shell-main > :not(.shell-bg) {
  position: relative;
  z-index: 1;
}

/* Most page roots set `min-height: calc(100vh - 80px)`, which forces extra
   scrollable height below short pages. Neutralize it so the content hugs its
   own height; .shell-main is flex:1 and carries the background, so the area
   below the content is filled seamlessly by the photo down to the bottom nav. */
.shell-main > :not(.shell-bg) {
  min-height: 0 !important;
}

.shell-theme-header {
  position: relative;
  order: 0;
  flex-shrink: 0;
  z-index: 1040;
  background: #e31e25;
  box-shadow: 0 10px 24px rgba(110, 7, 11, 0.22);
  /* Extend header behind the Android/iOS status bar */
  padding-top: env(safe-area-inset-top, 0px);
}

.shell-header-bar {
  padding: 0.6rem 0;
}

.shell-header-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.shell-brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.shell-brand img {
  width: 170px;
  max-width: 100%;
  display: block;
}

.shell-header-title {
  flex: 1;
  min-width: 0;
  color: #fff;
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  padding-left: 0.25rem;
}

.shell-header-title h1 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shell-header-branch {
  font-size: 0.88rem;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shell-toggler {
  flex-shrink: 0;
  margin-left: auto;        /* push to the right end of the header row */
  width: 42px;
  height: 42px;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  cursor: pointer;
}

.shell-toggler-bar {
  display: block;
  width: 20px;
  height: 2px;
  background: #fff;
  border-radius: 2px;
}

.shell-desktop-area {
  flex: 1;
  align-items: center;
  gap: 1.25rem;
}

.shell-side-top {
  color: #fff;
  display: flex;
  flex-direction: column;
  margin-right: 0.5rem;
}

.shell-page-title h1 {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #fff;
}

.shell-br-name {
  display: inline-flex;
  flex-direction: column;
  margin-top: 0.15rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
}

.shell-br-name span {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.72);
}

.shell-nav-list {
  list-style: none;
  margin: 0 0 0 auto;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.shell-nav-item {
  display: inline-flex;
}

.shell-nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 0.9rem;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  background: rgba(255, 255, 255, 0.09);
  box-shadow: 0 6px 14px rgba(130, 11, 16, 0.26);
}

.shell-nav-link img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.shell-nav-link:hover,
.shell-nav-link.active {
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}

.shell-mobile-bell {
  margin-left: auto;
}

.shell-mobile-bell + .shell-toggler {
  margin-left: 0.6rem;
}

.shell-drawer-count {
  margin-left: auto;
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 11px;
  background: #e31e25;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.shell-actions {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.shell-location-wrap {
  position: relative;
}

.shell-location,
.shell-logout {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 0;
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.shell-location {
  color: #fff;
  background: transparent;
}

.shell-location-menu {
  position: absolute;
  top: calc(100% + 0.55rem);
  right: 0;
  min-width: 220px;
  max-height: 400px;
  overflow-y: auto;
  padding: 0.45rem 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 32px rgba(61, 13, 15, 0.24);
  z-index: 1050;
}

.shell-location-menu__item {
  display: block;
  padding: 0.65rem 1rem;
  color: #2a2a2a;
  text-decoration: none;
  font-weight: 500;
}

.shell-location-menu__item:hover,
.shell-location-menu__item.active {
  background: rgba(227, 30, 37, 0.1);
  color: #a01218;
}

.shell-location__caret {
  font-size: 0.7rem;
  opacity: 0.85;
}

.shell-logout {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.7);
}

.shell-logout:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Mobile drawer */
.shell-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(82vw, 320px);
  min-height: 50vh;
  max-height: 85vh;
  background: #b81820;
  background-image: linear-gradient(180deg, #c4181f 0%, #9c1117 100%);
  z-index: 1050;
  /* Top padding clears the status bar / notch */
  padding: max(1.25rem, calc(env(safe-area-inset-top, 0px) + 0.5rem)) 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.28);
  overflow-y: auto;
  border-bottom-left-radius: 16px;
  height: auto;
}

.shell-drawer-header {
  color: #fff;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.shell-drawer-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.shell-drawer-sub {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-top: 0.15rem;
}

.shell-drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.shell-drawer-link {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);
  font-size: 0.95rem;
  font-weight: 600;
}

.shell-drawer-link img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.shell-drawer-link.active {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.shell-drawer-section-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
}
.shell-drawer-section-label.drawer-section-gap {
  margin-top: 0.75rem;
}
.shell-drawer-link i {
  width: 22px;
  text-align: center;
  font-size: 1.1rem;
}

.shell-drawer-branch {
  position: relative;
  margin-top: 0.25rem;
}

.shell-drawer-branch-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.65rem 1rem;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.shell-drawer-branch-list {
  max-height: 160px;
  overflow-y: auto;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.25);
  scrollbar-width: none;
}
.shell-drawer-branch-list::-webkit-scrollbar { display: none; }

.shell-drawer-branch-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  font-size: 0.78rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  -webkit-tap-highlight-color: transparent;
}
.shell-drawer-branch-item:last-child { border-bottom: none; }
.shell-drawer-branch-item.active {
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  font-weight: 700;
}
.shell-drawer-branch-item.disabled { opacity: 0.5; pointer-events: none; }

.shell-drawer-branch-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
}
.shell-drawer-branch-item.active .shell-drawer-branch-dot {
  background: #fff;
  border-color: #fff;
}

.shell-drawer-logout {
  flex-shrink: 0;
  margin-top: auto;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.15s;
}
.shell-drawer-logout:hover { background: rgba(255,255,255,0.2); }
.shell-drawer-logout i { font-size: 1.1rem; }

.shell-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1045;
}

.shell-drawer-enter-active,
.shell-drawer-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.shell-drawer-enter,
.shell-drawer-enter-from,
.shell-drawer-leave-to {
  transform: translateX(100%);
  opacity: 0.4;
}

/* Mobile bottom nav */
.shell-mobile-bottom {
  /* Static flex item kept at the bottom of the shell via order — not
     position:fixed, so it can't drift during iOS momentum scroll. */
  position: relative;
  order: 2;
  flex-shrink: 0;
  z-index: 1035;
  background: #E52320;
  border-top: none;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.25);
  padding: 0.4rem 0.5rem calc(0.4rem + env(safe-area-inset-bottom, 0px));
}

.shell-mobile-bottom-row {
  display: flex;
  align-items: stretch;
  gap: 0.25rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.shell-mobile-bottom-row::-webkit-scrollbar {
  display: none;
}

.shell-mobile-bottom-link {
  flex: 1 0 auto;
  min-width: 56px;
  min-height: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  padding: 0.4rem 0.25rem;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.1;
  transition: color 0.18s ease, background-color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.shell-mobile-bottom-link img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  filter: brightness(0) invert(1) opacity(0.75);
}

.shell-mobile-bottom-link.active {
  color: #ffffff;
  background: rgba(0, 0, 0, 0.2);
}

.shell-mobile-bottom-link.active img {
  filter: brightness(0) invert(1);
}

@media (max-width: 991.98px) {
  .shell-brand img {
    width: 120px;
  }

  /* Toggler tap target */
  .shell-toggler {
    width: 44px;
    height: 44px;
    -webkit-tap-highlight-color: transparent;
  }

  /* Header padding accounts for notch on landscape */
  .shell-header-bar {
    padding-left: max(0.6rem, env(safe-area-inset-left, 0px));
    padding-right: max(0.6rem, env(safe-area-inset-right, 0px));
  }
}

@media (max-width: 374px) {
  .shell-brand img {
    width: 76px;
  }

  .shell-header-title h1 {
    font-size: 0.95rem;
  }
}

@media (min-width: 992px) {
  .shell-drawer,
  .shell-drawer-backdrop,
  .shell-mobile-bottom,
  .shell-header-title {
    display: none !important;
  }

  .shell-main {
    padding-bottom: 0;
  }
}
</style>
